import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { ClientLayout, BlockedBanner, flattenWeeks, ExerciseMedia, isExternalMediaLink } from "./ClientHome";
import { toast } from "sonner";
import { Check, Play, X, Music } from "lucide-react";

export default function TrackWorkout() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [routineIdx, setRoutineIdx] = useState(0);
  const [exCursor, setExCursor] = useState(0);
  const [entries, setEntries] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [exerciseMedia, setExerciseMedia] = useState({});
  const [exerciseDesc, setExerciseDesc] = useState({});
  const [watchId, setWatchId] = useState(null);
  const [restLeft, setRestLeft] = useState(0);
  const [restTotal, setRestTotal] = useState(90);
  const [playlistId, setPlaylistId] = useState("");

  useEffect(() => {
    api.get("/settings/spotify-playlist").then((r) => setPlaylistId(r.data.playlist_id || "")).catch(() => {});
  }, []);

  const restEndRef = useRef(0);

  const startRest = (secs) => {
    const v = secs || restTotal || 90;
    setRestTotal(v);
    restEndRef.current = Date.now() + v * 1000;
    setRestLeft(v);
  };

  const beep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      o.start();
      o.stop(ctx.currentTime + 0.7);
    } catch {}
  };

  // timer basato su timestamp: continua a contare anche con l'app in background
  useEffect(() => {
    if (restLeft <= 0) return;
    const tick = () => {
      const left = Math.max(0, Math.round((restEndRef.current - Date.now()) / 1000));
      if (left === 0 && restLeft > 0) {
        beep();
        if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
      }
      setRestLeft(left);
    };
    tick();
    const id = setInterval(tick, 500);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", tick); };
  }, [restLeft > 0, restTotal]);

  useEffect(() => {
    api.get("/client/overview").then((r) => setOverview(r.data)).catch(() => setOverview({ user: { blocked: true } }));
    api.get("/client/logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/exercises").then((r) => {
      const m = {};
      const d = {};
      for (const e of r.data) {
        const key = (e.name || "").toLowerCase();
        if (e.video_url && !isExternalMediaLink(e.video_url)) {
          m[e._id] = e.video_url;
          m[key] = e.video_url;
        }
        if (e.description) {
          d[e._id] = e.description;
          d[key] = e.description;
        }
      }
      setExerciseMedia(m);
      setExerciseDesc(d);
    }).catch(() => {});
  }, []);

  const lastPerf = useMemo(() => {
    const map = {};
    for (const log of logs) {
      for (const e of log.entries || []) {
        if (!map[e.exercise_id]) {
          const done = (e.sets || []).filter((s) => s.done);
          if (done.length) map[e.exercise_id] = done[done.length - 1];
        }
      }
    }
    return map;
  }, [logs]);

  const blocked = !overview || overview.user?.blocked;
  const phase = !blocked ? overview.program?.phases?.[overview.current_phase_idx] : null;
  const weeks = flattenWeeks(phase);
  const week = weeks[overview?.current_week_idx ?? 0];
  const routines = week?.routines || [];
  const routine = routines[routineIdx];

  useEffect(() => {
    (routine?.exercises || []).forEach((ex) => {
      if (entries[ex.exercise_id]) return;
      const count = parseInt(ex.sets) || 3;
      setEntries((prev) => ({
        ...prev,
        [ex.exercise_id]: {
          exercise_id: ex.exercise_id,
          exercise_name: ex.name,
          sets: Array.from({ length: count }, () => ({ weight: "", reps: ex.reps || "", done: false })),
          notes: "",
        },
      }));
    });
  }, [routineIdx, overview]);

  if (!overview) return <ClientLayout><p className="text-muted-foreground">{t("common.loading")}</p></ClientLayout>;
  if (blocked) return <ClientLayout><BlockedBanner /></ClientLayout>;

  const setSet = (exId, i, patch) => {
    setEntries((prev) => {
      const entry = { ...prev[exId] };
      entry.sets = entry.sets.map((s, j) => (j === i ? { ...s, ...patch } : s));
      return { ...prev, [exId]: entry };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/client/logs", {
        routine_id: routine.id,
        routine_name: routine.name,
        phase_idx: overview.current_phase_idx,
        week_idx: overview.current_week_idx,
        entries: Object.values(entries),
        notes,
      });
      toast.success(t("track.saved"));
      navigate("/app/history");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ClientLayout>
      <div data-testid="track-workout-page" className="flex flex-col gap-4 max-w-xl mx-auto w-full">
        <h1 className="text-3xl font-black uppercase tracking-tight">{t("track.title")}</h1>
        {overview.program?.is_sample && (
          <div className="kc-card p-4 border-accent/40" data-testid="trial-sample-banner">
            <p className="text-sm text-muted-foreground leading-relaxed">{t("trial.sampleBanner")}</p>
            <button type="button" data-testid="trial-sample-unlock" onClick={() => navigate("/app/packages")}
              className="kc-btn-gold !px-5 !py-2 text-sm mt-3">{t("trial.sampleUnlock")}</button>
          </div>
        )}
        {routines.length === 0 ? (
          <p className="text-muted-foreground" data-testid="no-routine-notice">{t("track.noRoutine")}</p>
        ) : (
          <>
            <select data-testid="routine-select" value={routineIdx} onChange={(e) => { setRoutineIdx(Number(e.target.value)); setEntries({}); setExCursor(0); }}
              className="kc-input">
              {routines.map((r, i) => <option key={r.id} value={i}>{r.name}{r.day ? ` · ${r.day}` : ""}</option>)}
            </select>

            {playlistId && (
              <div className="kc-card p-4" data-testid="spotify-playlist-card">
                <p className="kc-eyebrow flex items-center gap-2 mb-3"><Music className="w-4 h-4" />{t("track.playlist")}</p>
                <iframe data-testid="spotify-embed" title="Spotify playlist"
                  src={`https://open.spotify.com/embed/playlist/${playlistId}?utm_source=generator&theme=0`}
                  className="w-full rounded-xl border-0" height="152"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" />
              </div>
            )}

            {(() => {
              const exs = routine?.exercises || [];
              const ex = exs[exCursor] || exs[0];
              if (!ex) return null;
              const entry = entries[ex.exercise_id];
              const last = lastPerf[ex.exercise_id];
              const isFirst = exCursor <= 0;
              const isLast = exCursor >= exs.length - 1;
              return (
                <div className="kc-card p-5" data-testid={`track-exercise-${ex.exercise_id}`}>
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <p className="kc-eyebrow" data-testid="track-exercise-progress">{t("track.exercise")} {exCursor + 1} / {exs.length}</p>
                    <div className="flex gap-1">
                      {exs.map((_, di) => (
                        <span key={di} className={`w-2 h-2 rounded-full ${di === exCursor ? "bg-primary" : di < exCursor ? "bg-accent" : "bg-border"}`} />
                      ))}
                    </div>
                  </div>
                  <p className="font-black text-2xl uppercase tracking-tight mt-2">{ex.name}</p>
                  <p className="text-sm font-mono text-primary font-bold mt-1">{ex.sets} × {ex.reps}{ex.rest ? ` · ${t("track.rest")} ${ex.rest}s` : ""}</p>
                  {(exerciseDesc[ex.exercise_id] || exerciseDesc[(ex.name || "").toLowerCase()]) && (
                    <p className="text-xs text-muted-foreground mt-1.5 leading-snug" data-testid={`track-desc-${ex.exercise_id}`}>
                      {exerciseDesc[ex.exercise_id] || exerciseDesc[(ex.name || "").toLowerCase()]}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    {last && <span className="kc-badge bg-secondary text-muted-foreground font-mono">{t("track.previous")}: {last.weight}kg×{last.reps}</span>}
                    {(exerciseMedia[ex.exercise_id] || exerciseMedia[(ex.name || "").toLowerCase()]) && (
                      <button type="button" data-testid={`watch-exec-${ex.exercise_id}`}
                        onClick={() => setWatchId(watchId === ex.exercise_id ? null : ex.exercise_id)}
                        className="text-xs font-bold text-primary inline-flex items-center gap-1">
                        <Play className="w-3.5 h-3.5" /> {watchId === ex.exercise_id ? t("track.hideExec") : t("track.watchExec")}
                      </button>
                    )}
                  </div>
                  {watchId === ex.exercise_id && (
                    <ExerciseMedia url={exerciseMedia[ex.exercise_id] || exerciseMedia[(ex.name || "").toLowerCase()]}
                      className="mt-3 rounded-xl w-full max-h-64 object-contain bg-secondary/40" />
                  )}
                  <div className="space-y-2.5 mt-4">
                    {entry?.sets.map((s, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-black shrink-0 ${s.done ? "bg-green-500 text-black" : "bg-secondary text-muted-foreground"}`}>{i + 1}</span>
                        <input data-testid={`weight-input-${ex.exercise_id}-${i}`} inputMode="decimal" placeholder={t("track.weight")}
                          value={s.weight} onChange={(e) => setSet(ex.exercise_id, i, { weight: e.target.value })}
                          className="kc-gym-input !h-14 !text-lg min-w-0" />
                        <input data-testid={`reps-input-${ex.exercise_id}-${i}`} inputMode="numeric" placeholder={t("track.reps")}
                          value={s.reps} onChange={(e) => setSet(ex.exercise_id, i, { reps: e.target.value })}
                          className="kc-gym-input !h-14 !text-lg min-w-0" />
                        <select data-testid={`rpe-select-${ex.exercise_id}-${i}`} aria-label="RPE"
                          value={s.rpe || ""} onChange={(e) => setSet(ex.exercise_id, i, { rpe: e.target.value })}
                          className="h-14 w-14 rounded-xl border-2 border-border bg-secondary text-center font-mono text-sm font-bold outline-none focus:border-primary transition-colors shrink-0">
                          <option value="">RPE</option>
                          {[6, 7, 8, 9, 10].map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                        <button data-testid={`workout-tracker-set-checkbox-${ex.exercise_id}-${i}`}
                          onClick={() => { setSet(ex.exercise_id, i, { done: !s.done }); if (!s.done) startRest(parseInt(ex.rest) || undefined); }}
                          className={`w-12 h-14 rounded-xl border-2 grid place-items-center transition-colors shrink-0 ${
                            s.done ? "bg-green-500 border-green-500 text-black" : "border-border text-transparent hover:border-green-500/60"
                          }`}>
                          <Check className="w-6 h-6" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <input data-testid={`exercise-notes-input-${ex.exercise_id}`} placeholder={t("track.notes")}
                    value={entry?.notes || ""}
                    onChange={(e) => setEntries((prev) => ({ ...prev, [ex.exercise_id]: { ...prev[ex.exercise_id], notes: e.target.value } }))}
                    className="kc-input mt-4 !py-2 text-sm" />
                  <div className="flex gap-3 mt-5">
                    <button data-testid="track-prev-exercise" onClick={() => setExCursor((c) => Math.max(0, c - 1))} disabled={isFirst}
                      className="kc-btn-ghost flex-1 !py-3.5 disabled:opacity-40">← {t("track.prevExercise")}</button>
                    {!isLast && (
                      <button data-testid="track-next-exercise" onClick={() => setExCursor((c) => Math.min(exs.length - 1, c + 1))}
                        className="kc-btn-primary flex-1 !py-3.5">{t("track.nextExercise")} →</button>
                    )}
                  </div>
                </div>
              );
            })()}

            {exCursor >= (routine?.exercises || []).length - 1 && (
              <>
                <textarea data-testid="workout-notes-input" placeholder={t("track.notes")} value={notes} rows={2}
                  onChange={(e) => setNotes(e.target.value)} className="kc-input" />
                <button data-testid="save-workout-button" onClick={save} disabled={saving} className="kc-btn-primary w-full !py-4 text-base">
                  {saving ? t("common.loading") : t("track.save")}
                </button>
              </>
            )}
            {restLeft > 0 && (
              <div data-testid="rest-timer-chip" className="fixed bottom-24 right-4 z-50 kc-card px-4 py-3 shadow-xl flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-2xl font-black text-primary" data-testid="rest-timer-value">
                    {Math.floor(restLeft / 60)}:{String(restLeft % 60).padStart(2, "0")}
                  </span>
                  <div>
                    <span className="text-xs uppercase tracking-wider text-muted-foreground block">{t("track.rest")}</span>
                    <div className="h-1.5 w-24 rounded-full bg-secondary overflow-hidden mt-1">
                      <div className="h-full bg-primary transition-all duration-1000" style={{ width: `${(restLeft / restTotal) * 100}%` }} data-testid="rest-timer-progress" />
                    </div>
                  </div>
                  <button data-testid="rest-timer-plus" onClick={() => { restEndRef.current += 30000; setRestLeft((s) => s + 30); setRestTotal((v) => v + 30); }} className="kc-btn-ghost !px-2 font-mono text-sm">+30</button>
                  <button data-testid="rest-timer-stop" onClick={() => setRestLeft(0)} className="kc-btn-ghost !px-2"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex gap-1.5" data-testid="rest-timer-presets">
                  {[60, 90, 120, 180].map((v) => (
                    <button key={v} data-testid={`rest-preset-${v}`} onClick={() => startRest(v)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                        restTotal === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"
                      }`}>
                      {Math.floor(v / 60)}:{String(v % 60).padStart(2, "0")}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ClientLayout>
  );
}
